import { createFileRoute, Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { ArrowLeft, Banknote, CreditCard, HandCoins, Smartphone } from 'lucide-react'

import { useEvent, useSupportReloads } from '@/lib/api/queries'
import type { SupportReloadRow } from '@/lib/api/types'
import { DEFAULT_EVENT_TIMEZONE, eventLocalToServerNaive } from '@/lib/eventTime'
import { DateFilter } from '@/components/ui/date-filter'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/events/$eventId/financials_/support-reloads')({
  component: SupportReloadsPage,
  // ?staff=email — deep link from the Financials Support Reloads table.
  validateSearch: (search: Record<string, unknown>): { staff?: string } => {
    const staff = typeof search.staff === 'string' && search.staff ? search.staff : undefined
    return staff ? { staff } : {}
  },
})

const METHODS = ['cash', 'standalone', 'terminal', 'manual'] as const
type Method = (typeof METHODS)[number]

const METHOD_META: Record<Method, { label: string; icon: typeof Banknote; chip: string }> = {
  cash: { label: 'Cash', icon: Banknote, chip: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' },
  standalone: { label: 'Standalone', icon: CreditCard, chip: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400' },
  terminal: { label: 'Terminal', icon: Smartphone, chip: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400' },
  // Rows recorded before the checkout started sending descriptions — a
  // staff reload whose cash/standalone split is unknown.
  manual: { label: 'Manual (unlabeled)', icon: HandCoins, chip: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300' },
}

const formatMoney = (value: number) =>
  value.toLocaleString(undefined, { style: 'currency', currency: 'USD' })

/** Pivot rows sharing a bucket key into {key -> method -> row}. */
function pivot<K extends string | number>(
  rows: SupportReloadRow[],
  keyOf: (row: SupportReloadRow) => K,
): Map<K, Partial<Record<Method, SupportReloadRow>>> {
  const out = new Map<K, Partial<Record<Method, SupportReloadRow>>>()
  for (const row of rows) {
    const key = keyOf(row)
    const bucket = out.get(key) ?? {}
    bucket[row.method] = row
    out.set(key, bucket)
  }
  return out
}

function SupportReloadsPage() {
  const { eventId } = Route.useParams()
  const { staff: staffFromSearch } = Route.useSearch()
  const eventIdNum = Number(eventId)
  const canViewFinancials = usePermission('can_view_financials')
  const { data: event } = useEvent(eventIdNum)

  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  // Click-through from the By Staff table: isolates every breakdown to one
  // support account (server-side filter).
  const [staffFilter, setStaffFilter] = useState<string | null>(staffFromSearch ?? null)

  // datetime-local values ('YYYY-MM-DDTHH:mm') — minute-level filtering.
  const queryParams = useMemo(() => {
    if (!startDate && !endDate && !staffFilter) return undefined
    const tz = event?.timezone || DEFAULT_EVENT_TIMEZONE
    const params: { created_at__gte?: string; created_at__lte?: string; staff?: string } = {}
    if (startDate) params.created_at__gte = eventLocalToServerNaive(startDate, tz)
    if (endDate) params.created_at__lte = eventLocalToServerNaive(endDate, tz)
    if (staffFilter) params.staff = staffFilter
    return params
  }, [startDate, endDate, staffFilter, event?.timezone])

  const { data: report, isLoading, error } = useSupportReloads(eventIdNum, queryParams)

  // Drill-down: a day click scopes From/To to that day; an hour click (only
  // meaningful once a single day is scoped) narrows to that hour. The
  // From/To inputs ARE the filter state, so the applied window stays
  // visible and clearable right there.
  const applyDayFilter = (date: string) => {
    setStartDate(`${date}T00:00`)
    setEndDate(`${date}T23:59`)
  }
  const singleDay =
    startDate && endDate && startDate.slice(0, 10) === endDate.slice(0, 10)
      ? startDate.slice(0, 10)
      : null
  // A single-hour window within the scoped day (hh:00–hh:59) — how the
  // hour drill-down writes it; hand-typed ranges matching it count too.
  const hourFilter =
    singleDay &&
    startDate.slice(11, 13) === endDate.slice(11, 13) &&
    startDate.slice(14, 16) === '00' &&
    endDate.slice(14, 16) === '59'
      ? Number(startDate.slice(11, 13))
      : null
  const applyHourFilter = (hour: number) => {
    if (!singleDay) return
    const hh = String(hour).padStart(2, '0')
    setStartDate(`${singleDay}T${hh}:00`)
    setEndDate(`${singleDay}T${hh}:59`)
  }

  if (!canViewFinancials) {
    return <AccessDenied message="You don't have permission to view financials." />
  }

  const summaryByMethod = new Map((report?.summary ?? []).map((row) => [row.method, row]))
  const presentMethods = METHODS.filter((method) => summaryByMethod.has(method))
  const totals = (report?.summary ?? []).reduce(
    (acc, row) => ({
      amount: acc.amount + row.amount,
      tokens: acc.tokens + row.tokens,
      count: acc.count + row.count,
      refunded: acc.refunded + (row.refunded_amount ?? 0),
      refundedTokens: acc.refundedTokens + (row.refunded_tokens ?? 0),
    }),
    { amount: 0, tokens: 0, count: 0, refunded: 0, refundedTokens: 0 },
  )
  const byDate = pivot(report?.by_date ?? [], (row) => row.date ?? '')
  const byHour = pivot(report?.by_hour ?? [], (row) => row.hour ?? 0)
  const byStaff = pivot(report?.by_staff ?? [], (row) => row.staff_email ?? 'Unknown')


  return (
    <div className="p-6 max-w-[1200px] mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Link
            to="/events/$eventId/financials"
            params={{ eventId }}
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline mb-2"
          >
            <ArrowLeft className="w-4 h-4" /> Financials
          </Link>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">Support Reloads</h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Staff-performed reloads by payment method. Amounts are gross
            collected (including taxes and fees).
          </p>
          {(staffFilter || singleDay) && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {staffFilter && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400">
                  {staffFilter}
                  <button
                    onClick={() => setStaffFilter(null)}
                    aria-label="Clear staff filter"
                    className="hover:text-blue-900 dark:hover:text-blue-200 font-bold"
                  >
                    ×
                  </button>
                </span>
              )}
              {singleDay && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400">
                  {singleDay}
                  <button
                    onClick={() => { setStartDate(''); setEndDate('') }}
                    aria-label="Clear day filter"
                    className="hover:text-green-900 dark:hover:text-green-200 font-bold"
                  >
                    ×
                  </button>
                </span>
              )}
              {hourFilter != null && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
                  {`${String(hourFilter).padStart(2, '0')}:00–${String((hourFilter + 1) % 24).padStart(2, '0')}:00`}
                  <button
                    onClick={() => {
                      // Step back to the full day, keeping the day filter.
                      setStartDate(`${singleDay}T00:00`)
                      setEndDate(`${singleDay}T23:59`)
                    }}
                    aria-label="Clear hour filter"
                    className="hover:text-amber-900 dark:hover:text-amber-200 font-bold"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
        <DateFilter
          value={{ gte: startDate || undefined, lte: endDate || undefined }}
          onChange={(v) => { setStartDate(v.gte ?? ''); setEndDate(v.lte ?? '') }}
          eventTimeZone={event?.timezone}
          defaultMonth={event?.start_date_time ? new Date(event.start_date_time) : undefined}
        />
      </div>

      {error ? (
        <p className="text-sm text-red-600 dark:text-red-400">Failed to load support reloads.</p>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : !report || report.summary.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-neutral-500 dark:text-neutral-400">
            No support reloads in this period.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Method summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {presentMethods.map((method) => {
              const row = summaryByMethod.get(method)!
              const meta = METHOD_META[method]
              const Icon = meta.icon
              return (
                <Card key={method}>
                  <CardContent className="pt-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full', meta.chip)}>
                        <Icon className="w-3.5 h-3.5" /> {meta.label}
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                      {formatMoney(row.amount)}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                      {row.tokens.toLocaleString()} tokens · {row.count.toLocaleString()} reloads
                    </p>
                    {((row.refunded_amount ?? 0) > 0 || (row.refunded_tokens ?? 0) > 0) && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        −{formatMoney(row.refunded_amount)} · {row.refunded_tokens.toLocaleString()} tokens
                        {' '}refunded · net {formatMoney(row.amount - row.refunded_amount)}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </div>

          <BreakdownTable
            title="By Staff Account"
            rowLabel="Support account"
            methods={presentMethods}
            buckets={[...byStaff.entries()].sort(([a], [b]) => String(a).localeCompare(String(b)))}
            renderKey={(key) => String(key)}
            onRowClick={(key) => setStaffFilter(String(key))}
          />

          <BreakdownTable
            title="By Day"
            rowLabel="Date"
            methods={presentMethods}
            buckets={[...byDate.entries()].sort(([a], [b]) => String(a).localeCompare(String(b)))}
            renderKey={(key) => String(key)}
            onRowClick={(key) => applyDayFilter(String(key))}
          />

          <BreakdownTable
            title="By Hour of Day"
            rowLabel="Hour"
            methods={presentMethods}
            buckets={[...byHour.entries()].sort(([a], [b]) => Number(a) - Number(b))}
            onRowClick={singleDay ? (key) => applyHourFilter(Number(key)) : undefined}
            renderKey={(key) => {
              const hour = Number(key)
              const next = (hour + 1) % 24
              return `${String(hour).padStart(2, '0')}:00–${String(next).padStart(2, '0')}:00`
            }}
          />


          <p className="text-xs text-neutral-400 dark:text-neutral-500">
            Totals: {formatMoney(totals.amount)} gross · {totals.tokens.toLocaleString()} tokens ·{' '}
            {totals.count.toLocaleString()} reloads
            {(totals.refunded > 0 || totals.refundedTokens > 0) && (
              <>
                {' '}· {formatMoney(totals.refunded)} / {totals.refundedTokens.toLocaleString()} tokens refunded
                {' '}· net {formatMoney(totals.amount - totals.refunded)} /{' '}
                {(totals.tokens - totals.refundedTokens).toLocaleString()} tokens
              </>
            )}
            . Table totals (dollars and tokens) are net of refunds. "Manual (unlabeled)"
            rows predate method tracking and are staff reloads whose cash/standalone
            split is unknown.
          </p>
        </>
      )}
    </div>
  )
}

function BreakdownTable({ title, rowLabel, methods, buckets, renderKey, onRowClick }: {
  title: string
  rowLabel: string
  methods: readonly Method[]
  buckets: [string | number, Partial<Record<Method, SupportReloadRow>>][]
  renderKey: (key: string | number) => string
  onRowClick?: (key: string | number) => void
}) {
  if (buckets.length === 0) return null
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-neutral-800">
              <th className="text-left font-medium py-2 pr-4">{rowLabel}</th>
              <th className="text-right font-medium py-2 px-3">Tokens</th>
              {methods.map((method) => (
                <th key={method} className="text-right font-medium py-2 px-3">
                  {METHOD_META[method].label}
                </th>
              ))}
              <th className="text-right font-medium py-2 px-3">Refunded</th>
              <th className="text-right font-medium py-2 pl-3">Total</th>
            </tr>
          </thead>
          <tbody>
            {buckets.map(([key, bucket]) => {
              const gross = methods.reduce((sum, method) => sum + (bucket[method]?.amount ?? 0), 0)
              const refunded = methods.reduce((sum, method) => sum + (bucket[method]?.refunded_amount ?? 0), 0)
              const refundedTokens = methods.reduce((sum, method) => sum + (bucket[method]?.refunded_tokens ?? 0), 0)
              // Net of refunds — dollars AND tokens — so every displayed
              // total foots against the summary cards' nets.
              const total = gross - refunded
              const totalTokens = methods.reduce((sum, method) => sum + (bucket[method]?.tokens ?? 0), 0) - refundedTokens
              return (
                <tr
                  key={String(key)}
                  onClick={onRowClick ? () => onRowClick(key) : undefined}
                  className={`border-b border-neutral-100 dark:border-neutral-800 last:border-0 ${
                    onRowClick ? 'cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/50' : ''
                  }`}
                >
                  <td className="py-2 pr-4 text-neutral-900 dark:text-neutral-100">{renderKey(key)}</td>
                  <td className="py-2 px-3 text-right text-neutral-600 dark:text-neutral-400">{totalTokens.toLocaleString()}</td>
                  {methods.map((method) => (
                    <td key={method} className="py-2 px-3 text-right text-neutral-600 dark:text-neutral-400">
                      {bucket[method] ? formatMoney(bucket[method]!.amount) : '—'}
                    </td>
                  ))}
                  <td className={cn(
                    'py-2 px-3 text-right',
                    refunded > 0 || refundedTokens > 0
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-neutral-400 dark:text-neutral-600',
                  )}>
                    {refunded > 0
                      ? `−${formatMoney(refunded)}`
                      // Token refunds that round to $0 (or a zero token
                      // value) must still explain the net-token movement.
                      : refundedTokens > 0
                        ? `−${refundedTokens.toLocaleString()} tokens`
                        : '—'}
                  </td>
                  <td className="py-2 pl-3 text-right font-medium text-neutral-900 dark:text-neutral-100">
                    {formatMoney(total)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}
